import React, { useMemo } from 'react';
import { typeBadge } from '../hygiene/types';
import Avatar from '../epics/Avatar';
import JiraKeyChip from '../JiraKeyChip';
import { FixPerson, FixRow, PROBLEM_LABEL, PROBLEM_TONE } from './types';
import * as fmt from '../../lib/format';

interface Props {
  rows: FixRow[];
  people: FixPerson[];
  loading: boolean;
  emptyText: string;
}

// Worklog entries that need a fix, grouped by the person who logged them. Each row shows the entry
// as written, what is wrong with it and what to write instead. The fix itself happens in Jira.
export default function FixList({ rows, people, loading, emptyText }: Props) {
  const groups = useMemo(() => {
    const byPerson = new Map<string, FixRow[]>();
    for (const r of rows) {
      const list = byPerson.get(r.person.account_id) ?? [];
      list.push(r);
      byPerson.set(r.person.account_id, list);
    }
    const order = new Map(people.map((p, i) => [p.account_id, i] as const));
    return Array.from(byPerson, ([id, list]) => ({
      id,
      rows: list,
      person: people.find((p) => p.account_id === id) ?? null,
      name: list[0].person.name ?? id,
    })).sort((a, b) => (order.get(a.id) ?? 1e9) - (order.get(b.id) ?? 1e9));
  }, [rows, people]);

  if (loading) {
    return (
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400" role="status" aria-label="Loading entries">
        {[0, 1, 2].map((i) => (
          <div key={i} className="animate-pulse border-t border-gray-100 px-5 py-4 first:border-t-0 motion-reduce:animate-none">
            <div className="h-3 w-24 rounded bg-gray-100" />
            <div className="mt-2 h-4 w-3/4 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white px-5 py-14 text-center text-sm text-gray-500 shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">{emptyText}</div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400">
      {groups.map((g) => (
        <section key={g.id} aria-label={g.name}>
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-gray-100 bg-gray-50/95 px-5 py-2.5 backdrop-blur first:border-t-0">
            <Avatar name={g.name} />
            <span className="text-sm font-semibold text-gray-900">{g.name}</span>
            {g.person && (
              <span className="ml-auto text-xs tabular-nums text-gray-500">
                {g.person.need_fix} of {g.person.entries} {g.person.entries === 1 ? 'entry' : 'entries'} to fix
                {g.person.valid_pct !== null ? ` · ${fmt.percent(g.person.valid_pct)} with one standard tag` : ''}
              </span>
            )}
          </div>
          <ul className="divide-y divide-gray-100">
            {g.rows.map((r) => (
              <li key={r.id} className="px-5 py-3.5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <JiraKeyChip issueKey={r.issue_key} url={r.jira_url} />
                      {r.issue_type && (
                        <span className={`rounded-full px-2 py-0.5 text-[0.6875rem] font-medium ring-1 ring-inset ${typeBadge(r.issue_type)}`}>{r.issue_type}</span>
                      )}
                      <span className="text-xs text-gray-500">{r.project_key}</span>
                    </div>
                    <p className="mt-1 line-clamp-1 text-sm leading-snug text-gray-600" title={r.issue_name ?? undefined}>
                      {r.issue_name || 'Untitled'}
                    </p>
                    <p className="mt-2 line-clamp-2 break-words rounded-lg bg-gray-50 px-3 py-2 text-sm leading-snug text-gray-900 ring-1 ring-inset ring-gray-200/70" title={r.comment ?? undefined}>
                      {r.comment ? r.comment : <span className="text-gray-500">No description</span>}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${PROBLEM_TONE[r.problem]}`}>{PROBLEM_LABEL[r.problem]}</span>
                      <span className="text-xs text-gray-600">{r.hint}</span>
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-xs tabular-nums text-gray-500">
                    <time dateTime={r.started}>{fmt.dayMonth(r.started)}</time>
                    <div className="mt-0.5">{fmt.hours(r.hours)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
